import React, { useEffect, useState, useCallback } from 'react';
import {
  scoutApi, ScoutWatchTrigger, ScoutTriggerCondition,
  ScoutInstrument, INSTRUMENTS, INSTRUMENT_DISPLAY,
} from '../../../services/api';
import { formatDateTimeUTC6 } from '../../../utils/formatters';

/** Render one condition as a plain-English sentence (no jargon). */
function describeCondition(c: ScoutTriggerCondition): string {
  const lvl = (n?: number) => (n != null ? `$${n.toFixed(4)}` : '?');
  switch (c.type) {
    case 'always_true':
      return 'fires immediately on the next watcher tick';
    case 'price_above':
      return `live mid price rises above ${lvl(c.level)}`;
    case 'price_below':
      return `live mid price drops below ${lvl(c.level)}`;
    case 'price_in_range':
      return `live mid price is between ${lvl(c.low)} and ${lvl(c.high)}`;
    case 'm1_close_above':
      return `a 1-min candle closes above ${lvl(c.level)}`;
    case 'm1_close_below':
      return `a 1-min candle closes below ${lvl(c.level)}`;
    case 'm15_close_above':
      return `a 15-min candle closes above ${lvl(c.level)} (sustained move, not a wick)`;
    case 'm15_close_below':
      return `a 15-min candle closes below ${lvl(c.level)} (sustained move, not a wick)`;
    case 'h1_close_above':
      return `a 1-hour candle closes above ${lvl(c.level)} (full-hour confirmation)`;
    case 'h1_close_below':
      return `a 1-hour candle closes below ${lvl(c.level)} (full-hour confirmation)`;
    case 'm1_is_bullish':
      return 'the current 1-min candle is green (close > open)';
    case 'm1_is_bearish':
      return 'the current 1-min candle is red (close < open)';
    case 'm1_body_pct_above':
      return `the 1-min candle has a strong body (≥ ${Math.round((c.pct ?? 0) * 100)}% of range)`;
    case 'm1_lower_wick_above':
      return `the 1-min candle has a long lower wick (price defended the low, ≥ ${Math.round((c.pct ?? 0) * 100)}% of range)`;
    case 'm1_upper_wick_above':
      return `the 1-min candle has a long upper wick (price rejected the high, ≥ ${Math.round((c.pct ?? 0) * 100)}% of range)`;
    case 'm1_wicked_below_then_reclaimed':
      return `price wicks below ${lvl(c.level)} in the last ${(c as any).window_bars ?? 15} minutes, then closes back above (sweep-reclaim from below)`;
    case 'm1_wicked_above_then_reclaimed':
      return `price wicks above ${lvl(c.level)} in the last ${(c as any).window_bars ?? 15} minutes, then closes back below (sweep-reclaim from above)`;
    case 'm1_volume_above_avg':
      return `1-min volume spikes (≥ ${((c as any).ratio ?? 1.5).toFixed(1)}× average of last ${(c as any).lookback_bars ?? 10} bars)`;
    case 'position_hold_hours_above':
      return `the trade has been open for more than ${(c as any).hours} hours`;
    case 'position_unrealized_pl_below':
      return `unrealized P/L drops below ${(c as any).threshold}`;
    case 'position_unrealized_pl_above':
      return `unrealized P/L rises above ${(c as any).threshold}`;
    case 'position_drawdown_atr_above':
      return `price moves at least ${(c as any).cents}¢ against entry`;
    default:
      return c.type;
  }
}

function timeUntil(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `in ${m}m`;
  return `in ${Math.floor(m / 60)}h ${m % 60}m`;
}

/** Group: latest entry + latest exit trigger per instrument (max 6 rows total). */
type TriggerSlot = { instrument: ScoutInstrument; kind: 'ENTRY' | 'EXIT'; trigger: ScoutWatchTrigger | null };

function buildSlots(triggers: ScoutWatchTrigger[]): TriggerSlot[] {
  const out: TriggerSlot[] = [];
  for (const inst of INSTRUMENTS) {
    for (const kind of ['ENTRY', 'EXIT'] as const) {
      const t = triggers.find(x => x.instrument === inst && (x.trigger_kind || 'ENTRY') === kind) ?? null;
      out.push({ instrument: inst, kind, trigger: t });
    }
  }
  return out;
}

const ScoutWatchTriggers: React.FC = () => {
  const [triggers, setTriggers] = useState<ScoutWatchTrigger[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTriggers(await scoutApi.getActiveTriggersLatest());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  // Only render slots that have a real ACTIVE trigger. Empty slots are hidden
  // entirely — the user wants a clean view, not placeholder filler.
  const slots = buildSlots(triggers).filter(s => s.trigger !== null);
  const activeCount = triggers.length;

  return (
    <div className="agent-signals-section">
      <div className="agent-signals-header">
        <span className="agent-signals-stats">
          <span className="agent-stat-total">
            {activeCount} active trigger{activeCount === 1 ? '' : 's'} (latest per instrument + kind)
          </span>
        </span>
        <button className="reviews-refresh-btn" onClick={fetch} disabled={loading}>
          {loading ? '...' : '↻ Refresh'}
        </button>
      </div>

      {error && <div className="advisor-error"><strong>Error:</strong> {error}</div>}

      {!loading && slots.length === 0 && !error && (
        <p className="agent-signals-empty">
          No active triggers. The watcher isn't currently watching for entries or exits on any
          instrument. The next strategist fire (every 30 min) will arm triggers if a setup prints.
        </p>
      )}

      <div className="triggers-list">
        {slots.map(({ instrument, kind, trigger }) => {
          // trigger is non-null here (filtered above), but TS doesn't know
          if (!trigger) return null;
          const expanded = expandedId === trigger.trigger_id;
          const lead = kind === 'ENTRY'
            ? `Will ${trigger.side ?? '?'} ${instrument} when ALL of:`
            : `Will close ${instrument} trade when ALL of:`;

          return (
            <div key={trigger.trigger_id} className="trigger-card trigger-status-active">
              <div
                className="trigger-card-header"
                onClick={() => setExpandedId(expanded ? null : trigger.trigger_id)}
                role="button"
              >
                <div className="trigger-card-title-row">
                  <span className="instrument-badge">{INSTRUMENT_DISPLAY[instrument]}</span>
                  <span className={`exit-decision-chip ${kind === 'ENTRY' ? 'exit-decision-armed' : 'exit-decision-hold'}`}>
                    {kind}
                  </span>
                  {trigger.side && (
                    <span className={`dir-tag ${trigger.side === 'BUY' ? 'dir-buy' : 'dir-sell'}`}>
                      {trigger.side}
                    </span>
                  )}
                  <strong className="trigger-name">
                    {trigger.name || trigger.setup_type || 'trigger'}
                  </strong>
                  {trigger.confidence != null && (
                    <span className="trigger-meta">conf {Math.round(trigger.confidence * 100)}%</span>
                  )}
                </div>
                <div className="trigger-card-meta-row">
                  <span className="trigger-meta trigger-meta-dim">
                    expires {timeUntil(trigger.expires_at)}
                  </span>
                  <span className="trigger-meta trigger-meta-dim">
                    created {formatDateTimeUTC6(trigger.created_at)}
                  </span>
                  <span className="trigger-card-chevron">{expanded ? '▾' : '▸'}</span>
                </div>
              </div>

              {/* Always-visible plain English summary (the whole point of this view) */}
              <div className="trigger-card-body" style={{ paddingTop: '0.4rem' }}>
                <div className="trigger-section">
                  <div className="trigger-plain-lead">{lead}</div>
                  <ul className="trigger-plain-list">
                    {trigger.conditions.map((c, i) => (
                      <li key={i}>{describeCondition(c)}</li>
                    ))}
                  </ul>
                  {kind === 'ENTRY' && trigger.trade_stop_price != null && (
                    <div className="trigger-meta trigger-meta-dim" style={{ marginTop: '0.4rem' }}>
                      Stop at ${trigger.trade_stop_price.toFixed(4)}
                      {trigger.trade_take_profit != null && (
                        <> · TP at ${trigger.trade_take_profit.toFixed(4)}</>
                      )}
                    </div>
                  )}
                  {kind === 'EXIT' && trigger.close_reason && (
                    <div className="trigger-meta trigger-meta-dim" style={{ marginTop: '0.4rem' }}>
                      Reason label: <code>{trigger.close_reason}</code>
                    </div>
                  )}
                </div>

                {expanded && trigger.reasoning && (
                  <div className="trigger-section">
                    <div className="trigger-section-title">Strategist reasoning</div>
                    <div className="trigger-reasoning">{trigger.reasoning}</div>
                  </div>
                )}
                {expanded && (
                  <div className="trigger-section">
                    <div className="trigger-meta trigger-meta-dim">
                      trigger_id: <code>{trigger.trigger_id}</code>
                      {trigger.trade_id && <> · trade_id: <code>{trigger.trade_id}</code></>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoutWatchTriggers;
